import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import logo from './assets/logo.png';
import spyImage from './assets/spy.jpeg';
import { categories, createInitialState, createRound, gameReducer, MAX_NAME_LENGTH, MAX_PLAYERS, MIN_PLAYERS, nameError } from './game/gameState';
import './GamePage.css';

const localImage = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

export default function GamePage() {
  const { t } = useTranslation();
  const [game, dispatch] = useReducer(gameReducer, undefined, createInitialState);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const inputRef = useRef(null);
  const titleRef = useRef(null);
  const player = game.players.find(item => item.id === game.openPlayerId);
  const isSpy = player && player.id === game.round?.spyId;
  const category = categories.find(item => item.id === game.category);

  useEffect(() => {
    titleRef.current?.focus();
    window.scrollTo(0, 0);
  }, [game.phase]);

  useEffect(() => {
    const conceal = () => dispatch({ type: 'concealRole' });
    const onVisibility = () => { if (document.hidden) conceal(); };
    window.addEventListener('blur', conceal);
    window.addEventListener('pagehide', conceal);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', conceal);
      window.removeEventListener('pagehide', conceal);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!['reveal', 'active'].includes(game.phase)) return;
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [game.phase]);

  const addPlayer = (event) => {
    event.preventDefault();
    const validation = nameError(name, game.players);
    setError(validation);
    if (validation) return;
    dispatch({ type: 'addPlayer', name });
    setName('');
    inputRef.current?.focus();
  };

  const start = () => {
    const round = createRound(game);
    if (round) dispatch({ type: 'start', round });
  };

  const finishReveal = () => dispatch({ type: 'finishReveal' });
  const title = { reveal: 'passThePhone', active: 'gameStarted', ended: 'roundEnded' }[game.phase];

  return (
    <main className="game-container" dir="rtl">
      <img src={logo} alt="Chthab — جذاب" className="game-logo" />
      {game.phase !== 'setup' && <h1 ref={titleRef} tabIndex={-1}>{t(title)}</h1>}

      {game.phase === 'setup' && (
        <section className="game-panel setup-panel" aria-labelledby="setup-title">
          <div className="section-heading">
            <h1 id="setup-title" ref={titleRef} tabIndex={-1}>{t('players')}</h1>
            <span dir="ltr">{game.players.length} / {MAX_PLAYERS}</span>
          </div>
          <form onSubmit={addPlayer} className="player-form">
            <label className="visually-hidden" htmlFor="player-name">{t('playerName')}</label>
            <div className="input-row">
              <input id="player-name" ref={inputRef} value={name} dir="auto"
                onChange={event => { setName(event.target.value); setError(null); }}
                placeholder={t('enterPlayerName')} maxLength={MAX_NAME_LENGTH} autoComplete="off"
                enterKeyHint="done" aria-invalid={!!error} aria-describedby={error ? 'name-error' : undefined}
                disabled={game.players.length >= MAX_PLAYERS} />
              <button className="add-player" type="submit" disabled={game.players.length >= MAX_PLAYERS}>{t('add')}</button>
            </div>
            {error && <p id="name-error" className="form-error" role="alert">{t(error)}</p>}
          </form>
          {game.players.length > 0 && (
            <ul className="player-list setup-players" aria-label={t('players')}>
              {game.players.map(item => (
                <li key={item.id}>
                  <bdi className="player-name">{item.name}</bdi>
                  <button className="remove-player" aria-label={t('removePlayer', { name: item.name })}
                    onClick={() => { dispatch({ type: 'removePlayer', id: item.id }); setError(null); }}>{t('remove')}</button>
                </li>
              ))}
            </ul>
          )}
          {game.players.length === MAX_PLAYERS && <p className="field-hint">{t('playersFull')}</p>}
          <label className="category-label" htmlFor="category">{t('chooseCategory')}</label>
          <div className="category-select">
            <select id="category" value={game.category} onChange={event => dispatch({ type: 'selectCategory', category: event.target.value })}>
              {categories.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
            <svg className="category-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
              <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p id="minimum-players" className="field-hint">{t('playerLimits', { min: MIN_PLAYERS, max: MAX_PLAYERS })}</p>
          <button className="primary-button full-width" onClick={start} disabled={game.players.length < MIN_PLAYERS} aria-describedby="minimum-players">{t('startGame')}</button>
        </section>
      )}

      {game.phase === 'reveal' && (
        <section className="game-panel" aria-label={t('passThePhone')}>
          <p className="game-intro">{t('revealHint')}</p>
          <ul className="player-list reveal-players">
            {game.players.map(item => {
              const viewed = game.viewedIds.includes(item.id);
              return (
                <li key={item.id}>
                  <button className={`player-reveal${viewed ? ' viewed' : ''}`} disabled={viewed}
                    onClick={() => dispatch({ type: 'showRole', id: item.id })}>
                    <bdi className="player-name">{item.name}</bdi>
                    <span className="player-status">{viewed ? `✓ ${t('viewed')}` : t('showRole')}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {game.phase === 'active' && (
        <section className="game-panel active-game">
          <div className="active-mark" aria-hidden="true">✓</div>
          <h2>{t('everyoneReady')}</h2>
          <p>{t('activeHint')}</p>
          <button className="danger-button full-width" onClick={() => setConfirmEnd(true)}>{t('endGame')}</button>
        </section>
      )}

      {game.phase === 'ended' && (
        <section className="game-panel end-game">
          <p className="field-hint">{category.label}</p>
          <p>{t('spyWas')}</p>
          <h2 className="result-name"><bdi>{game.players.find(item => item.id === game.round.spyId)?.name}</bdi></h2>
          <p>{t('secretWas')}</p>
          <h2>{game.round.location.name}</h2>
          <img className="location-image" src={localImage(game.round.location.image)} alt={game.round.location.name} />
          <button className="primary-button full-width" onClick={() => { dispatch({ type: 'playAgain' }); setName(''); setError(null); }}>{t('playAgain')}</button>
        </section>
      )}

      {game.phase === 'setup' && <Link className="back-home-link" to="/">{t('backToHome')}</Link>}

      {/* Mount private content only while its owner is viewing it. No close animation. */}
      {player && (
        <Modal isOpen onRequestClose={finishReveal} className="role-modal" overlayClassName="overlay"
          contentLabel={t('privateRole', { name: player.name })} shouldCloseOnOverlayClick={false}
          shouldReturnFocusAfterClose={false}>
          <div dir="rtl">
            <p className="private-player"><bdi>{player.name}</bdi></p>
            <h2>{isSpy ? t('spyRole') : t(`roleHeading.${game.round.category}`)}</h2>
            {!isSpy && <p className="secret-name">{game.round.location.name}</p>}
            <img className="location-image" src={isSpy ? spyImage : localImage(game.round.location.image)}
              alt={isSpy ? t('spyImage') : game.round.location.name} />
            <p className="role-hint">{isSpy ? t('spyHint') : t('regularHint')}</p>
            <div className="role-actions">
              <button className="primary-button full-width" autoFocus onClick={finishReveal}>{t('hideRole')}</button>
            </div>
          </div>
        </Modal>
      )}

      <Modal isOpen={confirmEnd} onRequestClose={() => setConfirmEnd(false)} className="confirm-modal" overlayClassName="overlay" contentLabel={t('endConfirm')}>
        <div dir="rtl">
          <h2>{t('endConfirm')}</h2>
          <p>{t('endConfirmHint')}</p>
          <div className="confirm-actions">
            <button className="secondary-button" autoFocus onClick={() => setConfirmEnd(false)}>{t('cancel')}</button>
            <button className="danger-button" onClick={() => { setConfirmEnd(false); dispatch({ type: 'end' }); }}>{t('endGame')}</button>
          </div>
        </div>
      </Modal>
    </main>
  );
}
